import React, { type ChangeEvent, forwardRef, useState } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { TextInputBase, type TextInputBaseProps } from '@instacart/ids-core'
import { borderRadius, colors } from '../../../foundation'

const HEX_COLOR_LENGTH = 7
const HEX_COLOR_REGEX = '#[a-fA-F0-9]{6}'

export interface Props extends Omit<TextInputBaseProps, 'onChange' | 'value'> {
  label: string
  inputLabel: string
  defaultValue?: string
}

const colorInputStyles = css({
  opacity: 0,
  width: '100%',
  height: '100%',
})

const Wrapper = styled.div<{ color: string }>(
  {
    top: 16,
    width: 24,
    height: 24,
    border: `1px solid ${colors.GRAYSCALE.X20}`,
    borderRadius: borderRadius.X4,
  },
  ({ color }) => ({ backgroundColor: color })
)

const wrapperStyles = css({
  position: 'relative',
})

export const FormColorInput = forwardRef<HTMLInputElement, Props>(
  ({ label, id, defaultValue, ...rest }, ref) => {
    const [text, setText] = useState(defaultValue || '')
    const [color, setColor] = useState(defaultValue || '#000000')

    const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setText(value)

      if (RegExp(HEX_COLOR_REGEX).test(value)) {
        setColor(value)
      }
    }

    const onColorChange = (event: ChangeEvent<HTMLInputElement>) => {
      setColor(event.target.value)
      setText(event.target.value)
    }

    return (
      <div css={wrapperStyles}>
        <TextInputBase
          {...rest}
          label={label}
          ref={ref}
          value={text}
          maxLength={HEX_COLOR_LENGTH}
          onChange={onTextChange}
          pattern={HEX_COLOR_REGEX}
          invalid={!!(text && !RegExp(HEX_COLOR_REGEX).test(text))}
          leadingIcon={
            <Wrapper color={color}>
              <input type="color" value={color} onChange={onColorChange} css={colorInputStyles} />
            </Wrapper>
          }
        />
      </div>
    )
  }
)
